import React, { useState, useEffect } from 'react';
import { KoreanTextModel, BookModel } from '../../../../models';
import { db } from '../../../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../../../../models/collectionNames';
import { Trash2, Plus, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { preprocessLatex } from '../../../../services/latexUtils';

interface ConceptExplainProps {
    unit: KoreanTextModel;
    bookId: string;
    activeBook: BookModel;
    onUpdate: (updatedFields: Partial<KoreanTextModel>) => void;
    draftTitle?: string;
    draftPassage?: string;
}

const ConceptExplain: React.FC<ConceptExplainProps> = ({ 
    unit, 
    bookId, 
    activeBook, 
    onUpdate,
    draftTitle,
    draftPassage
}) => {
    // Inline focus states
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);

    // Summary text state
    const [summaryText, setSummaryText] = useState(unit.conceptDescription || '');
    
    // Local concepts list state
    const [localConcepts, setLocalConcepts] = useState<any[]>(unit.concepts || []);

    // Sync states when unit changes
    useEffect(() => {
        setSummaryText(unit.conceptDescription || '');
        setLocalConcepts(unit.concepts || []);
    }, [unit]);

    const handleSaveSummary = async () => {
        if (summaryText === unit.conceptDescription) return;
        try {
            const docRef = doc(db, COLLECTIONS.BOOK_UNIT, unit.id);
            await updateDoc(docRef, {
                conceptDescription: summaryText,
                updatedAt: Date.now()
            });
            onUpdate({ conceptDescription: summaryText });
        } catch (err: any) {
            console.error('Failed to auto-save summary:', err);
        }
    };

    const handleConceptFieldChange = (idx: number, field: string, value: string) => {
        setLocalConcepts(prev => {
            const copy = [...prev];
            copy[idx] = {
                ...copy[idx],
                [field]: value
            };
            return copy;
        });
    };

    const handleSaveConceptsToDb = async (updatedConceptsList?: any[]) => {
        const listToSave = updatedConceptsList || localConcepts;
        try {
            const docRef = doc(db, COLLECTIONS.BOOK_UNIT, unit.id);
            await updateDoc(docRef, {
                concepts: listToSave,
                updatedAt: Date.now()
            });
            onUpdate({ concepts: listToSave });
        } catch (err: any) {
            console.error('Failed to auto-save concepts:', err);
        }
    };

    const handleDeleteConcept = async (idx: number) => {
        if (!window.confirm('이 개념 섹션을 삭제하시겠습니까?')) return;
        const newList = localConcepts.filter((_, i) => i !== idx);
        setLocalConcepts(newList);
        await handleSaveConceptsToDb(newList);
        if (activeCardIndex === idx) {
            setActiveCardIndex(null);
        }
    };

    const handleAddConcept = async () => {
        const newList = [...localConcepts, {
            title: `새 개념 섹션 ${localConcepts.length + 1}`,
            description: '',
            imageSvgs: [],
            imageCaptions: [],
            formulars: []
        }];
        setLocalConcepts(newList);
        await handleSaveConceptsToDb(newList);
        setActiveCardIndex(newList.length - 1);
    };

    const hasConcepts = localConcepts.length > 0;

    return (
        <div className="space-y-6 relative h-full flex flex-col overflow-hidden pb-4">
            {/* Header Toolbar */}
            <div className="flex items-center justify-between shrink-0 border-b border-slate-100 pb-3">
                <span className="text-xs font-black text-slate-700">개념 학습 자료 목록</span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleAddConcept}
                        className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black transition-colors shadow-sm"
                    >
                        <Plus size={11} />
                        <span>개념 수동 추가</span>
                    </button>
                </div>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-5 custom-scrollbar">
                {/* 1. Page Summary Section */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-1">
                    <div className="flex items-center justify-between select-none">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">단원/페이지 핵심 학습 목표</span>
                    </div>
                    {isEditingSummary ? (
                        <textarea
                            value={summaryText}
                            onChange={(e) => setSummaryText(e.target.value)}
                            onBlur={() => {
                                handleSaveSummary();
                                setIsEditingSummary(false);
                            }}
                            autoFocus
                            rows={2}
                            className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 custom-scrollbar"
                            placeholder="이 페이지를 관통하는 핵심 학습 목표를 입력하세요."
                        />
                    ) : (
                        <div 
                            onClick={() => setIsEditingSummary(true)}
                            className="text-xs font-bold text-slate-700 leading-relaxed cursor-pointer hover:bg-slate-200/40 p-2 rounded-xl transition-all"
                        >
                            {summaryText || '등록된 학습 목표가 없습니다. 클릭하여 입력해 주세요.'}
                        </div>
                    )}
                </div>

                {/* 2. Concepts List */}
                {!hasConcepts ? (
                    <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-slate-200 rounded-2xl space-y-4 bg-slate-50/50">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                            <FileText size={22} />
                        </div>
                        <div className="text-center">
                            <h4 className="text-xs font-black text-slate-800">개념 카드 정보가 비어있습니다</h4>
                            <p className="text-[10px] text-slate-400 font-bold mt-1 leading-relaxed">
                                '개념 수동 추가' 버튼을 눌러 새 개념 설명 카드를 직접 작성해 주세요.
                            </p>
                        </div>
                        <button
                            onClick={handleAddConcept}
                            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow-md transition-all active:scale-95"
                        >
                            <Plus size={12} />
                            <span>개념 수동 추가</span>
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {localConcepts.map((concept, idx) => {
                            const isEditing = activeCardIndex === idx;
                            return (
                                <div key={idx}>
                                    {isEditing ? (
                                        <div className="p-4 rounded-2xl border-2 border-indigo-500 bg-white shadow-md space-y-3 animate-in fade-in duration-200">
                                            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                                <span className="text-[9px] font-black text-indigo-600">개념 #{idx + 1} 편집 중</span>
                                                <button
                                                    onClick={() => handleDeleteConcept(idx)}
                                                    className="p-1 hover:bg-red-50 hover:text-red-500 rounded-md transition-all text-slate-400"
                                                    title="개념 카드 삭제"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">개념 제목</label>
                                                <input
                                                    type="text"
                                                    value={concept.title || ''}
                                                    onChange={(e) => handleConceptFieldChange(idx, 'title', e.target.value)}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">설명 및 공식 서술 (HTML/LaTeX 지원)</label>
                                                <textarea
                                                    rows={8}
                                                    value={concept.description || ''}
                                                    onChange={(e) => handleConceptFieldChange(idx, 'description', e.target.value)}
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 custom-scrollbar"
                                                />
                                            </div>
                                            <div className="flex justify-end pt-1">
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await handleSaveConceptsToDb();
                                                        setActiveCardIndex(null);
                                                    }}
                                                    className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black shadow-sm transition-colors"
                                                >
                                                    수정 완료
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div 
                                            onClick={() => setActiveCardIndex(idx)}
                                            className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/30 hover:border-indigo-200 hover:bg-white hover:shadow-sm transition-all space-y-3 cursor-pointer group/card relative"
                                        >
                                            {/* Title */}
                                            <div className="flex items-center justify-between border-b border-slate-100 pb-2 select-none">
                                                <h5 className="font-black text-xs text-indigo-600 flex items-center gap-1.5">
                                                    <span className="w-1 h-3.5 rounded-sm shrink-0 bg-indigo-600"></span>
                                                    {concept.title || `개념 ${idx + 1}`}
                                                </h5>
                                                <div className="flex items-center gap-1.5 opacity-0 group-hover/card:opacity-100 transition-opacity shrink-0">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteConcept(idx);
                                                        }}
                                                        className="p-1 hover:bg-red-50 hover:text-red-500 rounded-md transition-all text-slate-400"
                                                        title="개념 카드 삭제"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Grid layout for float SVG and description */}
                                            <div className="block flow-root">
                                                {/* Render SVG Diagrams */}
                                                {concept.imageSvgs && concept.imageSvgs.length > 0 && (
                                                    <div className="float-left mr-4 mb-3 w-[240px] flex flex-col gap-2 select-none" onClick={(e) => e.stopPropagation()}>
                                                        {concept.imageSvgs.map((svg: string, sIdx: number) => (
                                                            <div key={sIdx} className="flex flex-col gap-1 w-full">
                                                                <div className="w-full flex justify-center items-center overflow-hidden border border-slate-200 bg-white rounded-xl p-2.5 svg-diagram h-[130px]">
                                                                    <div 
                                                                        className="w-full h-full flex justify-center items-center [&_svg]:max-h-full [&_svg]:max-w-full [&_svg]:w-auto [&_svg]:h-auto [&_svg]:block [&_svg]:mx-auto" 
                                                                        dangerouslySetInnerHTML={{ __html: svg }} 
                                                                    />
                                                                </div>
                                                                {concept.imageCaptions && concept.imageCaptions[sIdx] && (
                                                                    <div className="text-[9px] font-black text-slate-400 text-center mt-0.5 break-keep">
                                                                        [그림] {concept.imageCaptions[sIdx]}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Description text with ReactMarkdown */}
                                                <div className="text-xs leading-relaxed text-slate-700 font-semibold break-keep [&>p]:m-0 [&>p]:mb-2">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkMath, remarkGfm]}
                                                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                    >
                                                        {preprocessLatex(concept.description || '')}
                                                    </ReactMarkdown>
                                                </div>

                                                {/* Formulars list */}
                                                {concept.formulars && concept.formulars.length > 0 && (
                                                    <div className="mt-3 overflow-hidden border border-slate-200 rounded-xl clear-both bg-white" onClick={(e) => e.stopPropagation()}>
                                                        <table className="w-full border-collapse text-left">
                                                            <tbody className="divide-y divide-slate-150">
                                                                {concept.formulars.map((form: any, fIdx: number) => (
                                                                    <tr key={fIdx} className="hover:bg-slate-50/30 text-[11px] leading-relaxed">
                                                                        <td className="py-1.5 px-3.5 w-[140px] font-black border-r border-slate-200 bg-slate-50/10 text-indigo-600 shrink-0">
                                                                            <ReactMarkdown
                                                                                remarkPlugins={[remarkMath, remarkGfm]}
                                                                                rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                                            >
                                                                                {preprocessLatex(form.title || '')}
                                                                            </ReactMarkdown>
                                                                        </td>
                                                                        <td className="py-1.5 px-3.5 font-bold text-slate-800 break-keep">
                                                                            <div className="flex flex-col gap-1">
                                                                                <div className="[&>p]:m-0">
                                                                                    <ReactMarkdown
                                                                                        remarkPlugins={[remarkMath, remarkGfm]}
                                                                                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                                                                                    >
                                                                                        {preprocessLatex(form.formula || '')}
                                                                                    </ReactMarkdown>
                                                                                </div>
                                                                                {form.formulaSvg && (
                                                                                    <div className="mt-0.5 flex justify-start items-center overflow-hidden bg-white border border-slate-100 rounded p-1 max-h-[40px] svg-formula [&>svg]:max-h-[30px] [&>svg]:w-auto">
                                                                                        <div dangerouslySetInnerHTML={{ __html: form.formulaSvg }} />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConceptExplain;
